// Angular Files
import { Injectable, Injector } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

// Shared Library Files
import {
    HelpServiceBase,
    HelpArticle,
    HelpTag,
    HelpConditional,
    HelpConditionContext
} from 'teller-online-libraries/help';

// Payment Integration Files
import { PaymentProcessorProvider } from 'apps/public-portal/src/app/payment-integrations';

// Teller Online Files
import { ServicesModule } from 'apps/public-portal/src/app/shared/services/services.module';

// Teller Online Library Files
import { TellerOnlineSiteMetadataService } from 'teller-online-libraries/core';

/**
 * Help tags specific to the public portal
 */
export enum PublicHelpTag {
    Checkout = 'checkout',
    Cart = 'cart',
    ManageProfile = 'manage-profile',
    Authentication = 'authentication',
    Search = 'search',
    PaymentMethods = 'payment-methods',
    PaymentHistory = 'payment-history'
}

@Injectable({
    providedIn: ServicesModule
})
export class HelpService extends HelpServiceBase {
    private _context: HelpConditionContext;

    constructor(
        sanitizer: DomSanitizer,
        injector: Injector
    ) {
        super(sanitizer);

        // Because the help service is used in _____ it can't load in other services, so we inject them afterwards to avoid circular dependencies
        const paymentProvider = injector.get(PaymentProcessorProvider);
        const siteMetadataService = injector.get(TellerOnlineSiteMetadataService);

        this._context = {
            tenantName: siteMetadataService.tenant,
            // TODO PROD-276: Re-evaluate if we can no longer assume Credit and E-Check are using the same processor
            processorName: paymentProvider.creditProcessorName,
            isRedirect: paymentProvider.activeCreditProcessor.redirect,
            authenticatedUsersEnabled: siteMetadataService.authenticationEnabled,
            tellerOnlineLiteEnabled: siteMetadataService.tellerOnlineLiteEnabled,
            scheduledPaymentsEnabled: siteMetadataService.scheduledPaymentsEnabled
        };

        this.initializeArticles();
    }

    protected getConditionContext(): HelpConditionContext {
        return this._context;
    }

    protected getArticleDefinitions(): HelpArticle[] {
        return HELP_ARTICLES;
    }

    public determineTagByUrl(currentUrl: string): HelpTag {
        // Determine which tag to show based on the current URL for public portal
        if (currentUrl.includes('payment-history') || currentUrl.includes('checkout/success')) {
            return PublicHelpTag.PaymentHistory;
        }

        if (currentUrl.includes('payment-methods')) {
            return PublicHelpTag.PaymentMethods;
        }

        if (currentUrl.includes('profile')) {
            return PublicHelpTag.ManageProfile;
        }

        if (currentUrl.includes('checkout')) {
            return PublicHelpTag.Checkout;
        }

        if (currentUrl.includes('sign')
            || currentUrl.includes('password')
            || currentUrl.includes('verify-email')) {
            return PublicHelpTag.Authentication;
        }

        return PublicHelpTag.Search;
    }

    public getAvailableTags(): HelpTag[] {
        // Return all tags from the PublicHelpTag enum
        return Object.values(PublicHelpTag);
    }
}

// Re-export types from shared library for backwards compatibility
export { HelpArticle, HelpConditional } from 'teller-online-libraries/help';
// Export public portal specific HelpTag enum (PublicHelpTag is exported above)

const HELP_ARTICLES: HelpArticle[] = [
    new HelpArticle({
        tag: PublicHelpTag.Cart,
        order: 0,
        bookmark: 'where-is-the-cart',
        title: 'Where is the cart?',
        body: '<p>The cart can be opened at any time by clicking the (:shopping_cart:) icon '
            + 'in the header at the top of the page. It is available on every page, next to '
            + 'the Help icon.</p>'
    }),
    new HelpArticle({
        tag: PublicHelpTag.Search,
        order: 1,
        bookmark: 'where-do-i-start',
        title: 'Where do I start?',
        body: "<p>To get started, from the home page, select one of the large white blocks below the banner. This will take you to a search page. See <a href='help#how-do-i-search'>How do I search?</a> for more details.</p>"
    }),
    new HelpArticle({
        tag: PublicHelpTag.Search,
        order: 2,
        bookmark: 'how-do-i-search',
        title: 'How do I search?',
        body: '<p>Select one of the search options from the list. Then enter any required (*) information and click <b>Search</b>.</p>'
    }),
    new HelpArticle({
        tag: PublicHelpTag.Search,
        order: 3,
        bookmark: 'no-results-found',
        title: 'Why does it say no results were found?',
        body: "<p>First, confirm you are entering the correct information into the correct fields. If you aren't sure where to find the information, please contact us.</p>"
            + '<p>If you are sure the information is correct, it is possible it may need to be paid for in person, please contact us.'
    }),
    new HelpArticle({
        tag: PublicHelpTag.Search,
        order: 4,
        bookmark: 'how-do-i-know-correct-item',
        title: "How do I know I'm paying for the correct thing?",
        body: '<p>Clicking on the name of the item in your cart will take you to a details page where you can confirm the item is the one you intended to pay for.</p>'
    }),
    new HelpArticle({
        tag: PublicHelpTag.Search,
        order: 5,
        bookmark: 'what-is-item-details',
        title: 'What is the item details page?',
        body: 'The item details page allows you to: <ul>'
            + '<li>Review the details of an item prior to payment.</li>'
            + '<li>Select a different payment option by clicking "Edit" next to the currently selected payment option.</li>'
            + '</ul>'
    }),
    new HelpArticle({
        tag: PublicHelpTag.Search,
        order: 6,
        bookmark: 'where-are-payment-options',
        title: 'What are payment options?',
        body: '<p>Payment options are the different amounts that you can pay towards an account.</p>'
    }),
    new HelpArticle({
        tag: PublicHelpTag.Cart,
        order: 6,
        bookmark: 'cart-warning',
        title: 'What is this warning?',
        body: '<p>When the price of an item you have in your cart has changed since you added it you will see a warning.</p>'
            + '<p>This is most often just a message informing you that there was a change.</p>'
            + '<p>Occasionally, you may be required to take action if the payment option you previously chose is no longer available.</p>'
    }),
    new HelpArticle({
        tag: PublicHelpTag.Checkout,
        order: 7,
        bookmark: 'zip-code-does-not-match',
        title: 'Why do I keep getting "Zip code does not match billing address"?',
        body: '<p>If you are receiving this message at checkout, the payment processor is indicating the zip code you have entered does not match the address that your card issuer has on file.</p>'
            + '<p>We recommend you double check the value you are entering is correct and confirm that your card issuer has the correct address.</p>'
    }),
    new HelpArticle({
        tag: PublicHelpTag.Checkout,
        order: 8,
        conditions: [HelpConditional.NotRedirect],
        bookmark: 'credit-card-rejected',
        title: 'Why is my credit card being rejected?',
        body: 'There are multiple reasons your credit card could be rejected. Please confirm the following:'
            + '<ul>'
            + '<li>You are entering the name that appears on the credit card.</li>'
            + '<li>You are entering the correct credit card number, expiry date (in the format MM/YY e.g. 08/22), and CVV.</li>'
            + '<li>You are entering the correct zip code that your credit card issuer has on file for your credit card.</li>'
            + '</ul>'
            + '<p>*If your credit card number is correct, you should see a small icon indicating the card type (Visa, MasterCard, Discover, Amex) next to the number.</p>'
    }), // TODO: This description fits celero but may not fit others
    new HelpArticle({
        tag: PublicHelpTag.Authentication,
        order: 9,
        conditions: [HelpConditional.Users],
        bookmark: 'how-do-i-create-account',
        title: 'How do I create an account?',
        body: '<p>To create an account, click the :person_add: <b>Sign Up</b> button, or use the Sign Up link provided in the email receipt of a previous payment.</p>'
            + '<p>Once you submit the Sign Up form, a verification code will be sent to the email you provided. Enter the code to confirm your account.</p>'
    }),
    new HelpArticle({
        tag: PublicHelpTag.ManageProfile,
        order: 10,
        conditions: [HelpConditional.Users],
        bookmark: 'manage-account',
        title: 'How can I manage my account settings?',
        body: '<p>Click on your username :person: (or the menu :menu:button on mobile) to access account settings.</p>'
            + 'From the drop down, you can:'
            + '<ul>'
            + '<li>Update your personal information</li>'
            + '<li>Manage notification settings</li>'
            + '<li>Change your password</li>'
            + '<li>Manage saved payment methods</li>'
            + '<li>View your payment history</li>'
            + '<li>Sign Out</li>'
            + '</ul>'
    }),
    new HelpArticle({
        tag: PublicHelpTag.Cart,
        order: 11,
        bookmark: 'how-remove-item-from-cart',
        title: 'How do I remove something from my cart?',
        body: 'You can remove an item from your cart by clicking the :remove_shopping_cart: button. This button can be found:'
            + '<ul>'
            + '<li>At the bottom of the item card in the search results.</li>'
            + '<li>At the bottom of the item card on the item details page.</li>'
            + '<li>In your cart next to the item that you want to remove.</li>'
            + '</ul>'
    }),
    new HelpArticle({
        tag: PublicHelpTag.Checkout,
        order: 12,
        bookmark: 'how-do-i-pay',
        title: 'How do I pay?',
        body: '<p>Open your cart if it\'s not currently open by clicking on the cart button (:shopping_cart:) '
            + 'in the header (at the top of the page).</p> '
            + `|condition:${HelpConditional.Redirect}|<p>At the bottom of the cart panel there is a field for you to provide an email, which is required so a receipt can be emailed to you.</p>`
            + `<p>After you have input an email address, you can click "Checkout" below the email field in order to proceed.</p>|endcondition:${HelpConditional.Redirect}|`
            + `|condition:${HelpConditional.NotRedirect}|<p>At the bottom of the cart panel there is a "Checkout" button which will take you to the checkout page.</p>`
            + `<p>Once there, you can choose how you would like to pay (Credit Card or E-Check), enter your payment details, and click the <b>Pay</b> button at the bottom.</p>|endcondition:${HelpConditional.NotRedirect}|`
    }),
    new HelpArticle({
        tag: PublicHelpTag.PaymentMethods,
        order: 13,
        conditions: [HelpConditional.NotRedirect],
        bookmark: 'what-are-saved-payment-methods',
        title: 'What are saved payment methods?',
        body: '<p>Saved payment methods are a way of storing a payment method (e.g. Credit Card, Debit Card and E-Check) for later use.</p>'
            + '<p>At checkout, you can select a saved payment method and click the <b>Pay</b> button.</p>'
    }),
    new HelpArticle({
        tag: PublicHelpTag.PaymentMethods,
        order: 14,
        bookmark: 'how-to-add-saved-payment-method',
        title: 'How do I add a saved payment method?',
        body: 'There are 2 ways to add a saved payment method.'
            + '<ul>'
            + '<li>You can use the <b>Save Payment Method</b> checkbox at checkout.</li>'
            + '<li>Navigate to the Payment Methods page and click the '
            + '<b>Add Payment Method</b> button at the top of the page.</li>'
            + '</ul>'
    }),
    new HelpArticle({
        tag: PublicHelpTag.PaymentMethods,
        order: 15,
        bookmark: 'how-to-update-saved-payment-method',
        title: 'How do I update a saved payment method?',
        body: 'In the Payment methods page, you can view your existing payment methods. To update a method, click the :edit: <b>Edit</b> button.'
    }),
    new HelpArticle({
        tag: PublicHelpTag.PaymentHistory,
        order: 16,
        bookmark: 'view-previous-payments',
        title: 'How can I view receipts from previous payments?',
        body: '<p>In the Payment History page, there is a list of all of your previous transactions. '
            + 'Click on a transaction to show further details.</p> <p>Click on the <b>Download Receipt</b> :receipt_long:'
            + 'button in the popup to download a copy of the receipt.</p>'
    }),
    new HelpArticle({
        tag: PublicHelpTag.Authentication,
        order: 17,
        conditions: [HelpConditional.Users],
        bookmark: 'forgot-password',
        title: 'What do I do if I forgot my password?',
        body: '<p>If you have forgotten your password, click the <em>Forgot your password?</em> link on the Sign In page, enter the email address associated with your account, '
            + 'and click the <b>Continue</b> button.</p> <p>Enter the verification code sent to your email address and create a new password for your account. '
            + 'Confirm your password and click the <b>Reset Password</b> button.</p>'
    }),
    new HelpArticle({
        tag: PublicHelpTag.Authentication,
        order: 18,
        conditions: [HelpConditional.Users],
        bookmark: 'verification-code-not-working',
        title: 'Why is my verification code not working?',
        body: "<p>Verification codes are sent via text message or email automatically. In the case that you didn't receive a code, or the code is not working, you can click <em>Try again</em> (under the code field) after 60 seconds.</p>"
            + "<p>If the verification code still isn't working, confirm that you are using the most recent code that you have received.</p>"
    }),
    new HelpArticle({
        tag: PublicHelpTag.Search,
        order: 19,
        conditions: [HelpConditional.Tenant],
        tenants: ['santaclara'],
        bookmark: 'how-to-pay-for-multiple-items',
        title: 'How do I pay for multiple properties in one transaction?',
        body: '<p>You can pay for multiple properties by first adding them to your cart (:shopping_cart:)</p>'
            + '<ul>'
            + '<li>Go to Home (:home:)</li>'
            + '<li>Search for the first APN you want to pay</li>'
            + '<li>In the search results on the Item Details page, find the item to pay</li>'
            + '<li>Add it to the cart (:shopping_cart:)</li>'
            + '<li>Return to Home (:home:)</li>'
            + '<li>Repeat.</li>'
            + '</ul>'
            + '<p>Once desired properties are in the Cart, click Checkout and choose a Payment Method.  Up to 30 items can be paid in one transaction.</p>'
            + '<p>Note: All items in the Cart will be paid.  To exclude an APN or Assessment #, remove it from the Cart by clicking the (:remove_shopping_cart:) icon.</p>'
    })
];
